import { SetStateAction, useCallback, useContext, useMemo } from 'react'
import { Position } from '../../types'
import { CutterContext } from '../contexts/CutterContext'
import { validateVisibleRange } from '../functions/validateVisibleRange'
import clamp from 'lodash/clamp'

export function useZoom(): [number, (value: SetStateAction<number>, position?: Position) => void] {
  const {
    visibleRange,
    setVisibleRange,
    audio: { length },
  } = useContext(CutterContext)

  const zoom = useMemo(() => length / (visibleRange[1] - visibleRange[0]), [visibleRange, length])
  const setZoom = useCallback(
    (value: SetStateAction<number>, cursorPosition?: Position) => {
      setVisibleRange((range) => {
        const diff = range[1] - range[0]
        const zoom = length / diff
        const nextZoom = Math.max(typeof value === 'function' ? value(zoom) : value, 1)
        const nextDiff = length / nextZoom
        const diffDelta = nextDiff - diff
        const position =
          typeof cursorPosition === 'number'
            ? cursorPosition
            : range[0] === 0
              ? 0
              : range[1] === length
                ? length
                : (range[0] + range[1]) / 2
        const positionRatio = (clamp(position, ...range) - range[0]) / (range[1] - range[0])

        return validateVisibleRange(
          [range[0] - diffDelta * positionRatio, range[1] + diffDelta * (1 - positionRatio)],
          length,
        )
      })
    },
    [length, setVisibleRange],
  )

  return [zoom, setZoom]
}
